import { Reducer } from 'redux'
import { INewReportRequest, IReportState, ReportTypes } from './types'
import { createReducer } from 'reduxsauce'
import { failure, IActionType, IPaginator, request, success } from '../../root.types'
import IntradayHeartRate from '../../../application/models/time.series/intraday/heart.rate/intraday.heart.rate'
import TimeSeries from '../../../application/models/time.series/time.series'
import Report from '../../../application/models/report/report'
import DataSetItem from '../../../application/models/time.series/data.set.item'
import { Type } from '../../../../containers/users/patients/report'
import Sleep, { SleepType } from '../../../application/models/activity/sleep/sleep'
import SleepStagesSummary from '../../../application/models/activity/sleep/stages.summary'
import Comorbidity from '../../../application/models/medical.indicator/comorbidities'
import Medicament from '../../../application/models/medical.indicator/medicament'

const hasQuestApp: boolean = Boolean(process.env.REACT_APP_QUEST_APP || '')

export const INITIAL_STATE: IReportState = {
    dialog: false,
    create: {
        report: new Report(),
        loading: false,
        error: false,
        success: false,
    },
    list: {
        report: [],
        loading: false,
        error: false,
        success: false,
        paginator: {
            first: 0,
            rows: 10,
            page: 0,
            pageCount: 0,
            totalRecords: 0,
            search: {
                key: '',
                value: ''
            }
        }
    },
    remove: {
        ids: [],
        dialog: false,
        loading: false,
        success: false,
        error: false
    },
    depression: '',
    frailtySyndrome: '',
    muscleStrength: '',
    nocturiaPresence: '',
    sarcopenia: '',
    sleepiness: '',
    startDate: undefined,
    endDate: undefined,
    comparatorReport: undefined,
    steps: {
        data: new TimeSeries(),
        loading: false,
        error: false,
        success: false
    },
    activeMinutes: {
        data: new TimeSeries(),
        loading: false,
        error: false,
        success: false
    },
    heartRate: {
        data: [],
        loading: false,
        error: false,
        success: false
    },
    sleep: {
        data: [],
        loading: false,
        success: false,
        error: false,
    },
    prevSteps: {
        total: 0,
        average: 0,
        loading: false,
        error: false,
        success: false
    },
    prevActiveMinutes: {
        total: 0,
        average: 0,
        loading: false,
        error: false,
        success: false
    },
    prevHeartRate: {
        total: 0,
        average: 0,
        loading: false,
        error: false,
        success: false
    },
    prevSleep: {
        total: 0,
        average: 0,
        awake: 0,
        rem: 0,
        light: 0,
        deep: 0,
        loading: false,
        error: false,
        success: false
    },
    view: {
        bmi: true,
        waistCircumference: !hasQuestApp,
        calfCircumference: !hasQuestApp,
        handGrid: !hasQuestApp,
        gaitSpeed: !hasQuestApp,
        nocturia: !hasQuestApp,
        depression: !hasQuestApp,
        sleepiness: !hasQuestApp,
        fragility: !hasQuestApp,
        sarcopenia: !hasQuestApp,
        muscleStrength: !hasQuestApp,
        steps: true,
        activeMinutes: true,
        heartRate: true,
        sleep: true,
        signature: true
    },
    comorbidities: {
        data: new Comorbidity(),
        loading: false,
        error: false,
        success: false
    },
    medicaments: {
        data: new Medicament(),
        loading: false,
        error: false,
        success: false
    }
}

/* Steps reducers */
export const reset = (state: IReportState = INITIAL_STATE) => {
    return {
        ...state,
        list: INITIAL_STATE.list
    }
}

export const changeListPaginator = (
    state: IReportState = INITIAL_STATE,
    action: IActionType<{ paginator: IPaginator }>) => {
    const { paginator } = action.payload
    return {
        ...state,
        list: { ...state.list, paginator }
    }
}

export const loadStepsRequest = (state: IReportState = INITIAL_STATE,
    action: IActionType<{ type: string }>) => {
    const { type } = action.payload
    if (type === Type.CURRENT) {
        return {
            ...state,
            steps: request(state.steps)
        }
    }
    else if (type === Type.PREVIOUS) {
        return {
            ...state,
            prevSteps: request(state.prevSteps)
        }
    }
    return state

}

export const loadStepSuccess = (
    state: IReportState = INITIAL_STATE,
    action: IActionType<{ data: TimeSeries, type: string }>) => {
    const { data, type } = action.payload

    if (type === Type.CURRENT) {
        return {
            ...state,

            steps: success({ ...state.steps, data })
        }
    }
    else if (type === Type.PREVIOUS) {
        const total: number = data
            ?.data_set
            ?.map((dataItem: DataSetItem) => dataItem.value || 0)
            ?.reduce((accumulated: number, current: number) => accumulated + current, 0) || 0

        const amount_values: DataSetItem[] = data.data_set
            ?.filter((dataValue) => dataValue.value && !Number.isNaN(dataValue.value)) || []

        const average: number = Math.round(total / (amount_values.length || 1))

        return {
            ...state,
            prevSteps: success({ ...state.prevSteps, total, average })
        }
    }
    return state
}

export const loadStepsFailure = (state: IReportState = INITIAL_STATE,
    action: IActionType<{ type: string }>) => {
    const { type } = action.payload
    if (type === Type.CURRENT) {
        return {
            ...state,
            steps: failure(state.steps)
        }
    }
    else if (type === Type.PREVIOUS) {
        return {
            ...state,
            prevSteps: failure(state.prevSteps)
        }
    }
    return state
}


/* Steps reducers */
export const loadActiveMinutesRequest = (state: IReportState = INITIAL_STATE,
    action: IActionType<{ type: string }>) => {
    const { type } = action.payload
    if (type === Type.CURRENT) {
        return {
            ...state,
            activeMinutes: request(state.activeMinutes)
        }
    }
    else if (type === Type.PREVIOUS) {
        return {
            ...state,
            prevActiveMinutes: request(state.prevActiveMinutes)
        }
    }
    return state
}

export const loadActiveMinutesSuccess = (
    state: IReportState = INITIAL_STATE,
    action: IActionType<{ data: TimeSeries, type: string }>) => {
    const { data, type } = action.payload

    if (type === Type.CURRENT) {
        return {
            ...state,

            activeMinutes: success({ ...state.activeMinutes, data })
        }
    }
    else if (type === Type.PREVIOUS) {
        const values: number[] = data
            ?.data_set
            ?.map((dataItem: DataSetItem) => Number(dataItem.value))
            ?.filter((dataValue: number) => dataValue && !Number.isNaN(dataValue))
            || []

        const total: number = values
            ?.reduce((accumulated: number | undefined, current: number | undefined) =>
                (accumulated || 0) + (current || 0),
                0
            ) || 0

        const average: number = Math.round(total / (values?.length || 1))

        return {
            ...state,
            prevActiveMinutes: success({ ...state.prevActiveMinutes, total, average })
        }
    }
    return state
}

export const loadActiveMinutesFailure = (state: IReportState = INITIAL_STATE,
    action: IActionType<{ type: string }>) => {
    const { type } = action.payload
    if (type === Type.CURRENT) {
        return {
            ...state,
            activeMinutes: failure(state.activeMinutes)
        }
    }
    else if (type === Type.PREVIOUS) {
        return {
            ...state,
            prevActiveMinutes: failure(state.prevActiveMinutes)
        }
    }
    return state
}

/* Heart rate reducers */

export const loadHeartRateRequest = (state: IReportState = INITIAL_STATE,
    action: IActionType<{ type: string }>) => {
    const { type } = action.payload
    if (type === Type.CURRENT) {
        return {
            ...state,
            heartRate: request(state.heartRate)
        }
    }
    else if (type === Type.PREVIOUS) {
        return {
            ...state,
            prevHeartRate: request(state.prevHeartRate)
        }
    }
    return state
}

export const loadHearRateSuccess = (
    state: IReportState = INITIAL_STATE,
    action: IActionType<{ data: IntradayHeartRate[], type: string }>) => {
    const { data, type } = action.payload

    if (type === Type.CURRENT) {
        return {
            ...state,
            heartRate: success({ ...state.heartRate, data })
        }
    }
    else if (type === Type.PREVIOUS) {
        const amount_values: number[] = data.map((dataItem: IntradayHeartRate) => Number(dataItem.summary?.average))
            ?.filter((dataValue: number) => dataValue && !Number.isNaN(dataValue))

        const total: number = amount_values
            ?.reduce((accumulated: number, current: number) => accumulated + current, 0) || 0


        const average: number = Math.round(total / (amount_values.length || 1))

        return {
            ...state,
            prevHeartRate: success({ ...state.prevHeartRate, total, average })
        }
    }
    return state
}

export const loadHeartRateFailure = (state: IReportState = INITIAL_STATE,
    action: IActionType<{ type: string }>) => {
    const { type } = action.payload
    if (type === Type.CURRENT) {
        return {
            ...state,
            heartRate: failure(state.heartRate)
        }
    }
    else if (type === Type.PREVIOUS) {
        return {
            ...state,
            prevHeartRate: failure(state.prevHeartRate)
        }
    }
    return state
}


export const loadSleepRequest = (state: IReportState = INITIAL_STATE
    , action: IActionType<{ type: string }>) => {
    const { type } = action.payload
    if (type === Type.CURRENT) {
        return {
            ...state,
            sleep: request(state.sleep)
        }
    }
    else if (type === Type.PREVIOUS) {
        return {
            ...state,
            prevSleep: request(state.prevSleep)
        }
    }
    return state
}

export const loadSleepSuccess = (
    state: IReportState = INITIAL_STATE,
    action: IActionType<{ data: Sleep[], type: string }>) => {
    const { data, type } = action.payload

    if (type === Type.CURRENT) {
        return {
            ...state,
            sleep: success({ ...state.sleep, data })
        }
    } else if (type === Type.PREVIOUS) {

        const stagesSleeps: Sleep[] = data
            ?.filter((sleepItem: Sleep) => sleepItem.type === SleepType.STAGES)

        const total: number = stagesSleeps
            ?.map((sleepItem: Sleep) => Number(sleepItem.duration))
            ?.filter((dataValue: number) => dataValue && !Number.isNaN(dataValue))
            ?.reduce((accumulated: number | undefined, current: number | undefined) =>
                (accumulated || 0) + (current || 0),
                0
            ) || 0

        const totalAwake: number = stagesSleeps
            ?.map((sleepItem: Sleep) => {
                if (sleepItem.pattern?.summary instanceof SleepStagesSummary) {
                    return Number(sleepItem.pattern?.summary?.awake?.duration)
                }
                return 0
            })
            ?.filter((dataValue: number) => dataValue && !Number.isNaN(dataValue))
            ?.reduce((accumulated: number | undefined, current: number | undefined) =>
                (accumulated || 0) + (current || 0),
                0
            ) || 0

        const totalRem: number = stagesSleeps
            ?.map((sleepItem: Sleep) => {
                if (sleepItem.pattern?.summary instanceof SleepStagesSummary) {
                    return Number(sleepItem.pattern?.summary?.rem?.duration)
                }
                return 0
            })
            ?.filter((dataValue: number) => dataValue && !Number.isNaN(dataValue))
            ?.reduce((accumulated: number | undefined, current: number | undefined) =>
                (accumulated || 0) + (current || 0),
                0
            ) || 0

        const totalLight: number = stagesSleeps
            ?.map((sleepItem: Sleep) => {
                if (sleepItem.pattern?.summary instanceof SleepStagesSummary) {
                    return Number(sleepItem.pattern?.summary?.light?.duration)
                }
                return 0
            })
            ?.filter((dataValue: number) => dataValue && !Number.isNaN(dataValue))
            ?.reduce((accumulated: number | undefined, current: number | undefined) =>
                (accumulated || 0) + (current || 0),
                0
            ) || 0

        const totalDeep: number = stagesSleeps
            ?.map((sleepItem: Sleep) => {
                if (sleepItem.pattern?.summary instanceof SleepStagesSummary) {
                    return Number(sleepItem.pattern?.summary?.deep?.duration)
                }
                return 0
            })
            ?.filter((dataValue: number) => dataValue && !Number.isNaN(dataValue))
            ?.reduce((accumulated: number | undefined, current: number | undefined) =>
                (accumulated || 0) + (current || 0),
                0
            ) || 0

        const awake: number = parseFloat(totalAwake ? Number((totalAwake * 100) / total).toFixed(2) : '0')
        const rem: number = parseFloat(totalRem ? Number((totalRem * 100) / total).toFixed(2) : '0')
        const light: number = parseFloat(totalLight ? Number((totalLight * 100) / total).toFixed(2) : '0')
        const deep: number = parseFloat(totalDeep ? Number((totalDeep * 100) / total).toFixed(2) : '0')


        return {
            ...state,
            prevSleep: success({ ...state.prevSleep, total, average: total / (stagesSleeps.length || 1), awake, rem, light, deep })
        }
    }
    return state
}

export const loadSleepFailure = (state: IReportState = INITIAL_STATE,
    action: IActionType<{ type: string }>) => {
    const { type } = action.payload
    if (type === Type.CURRENT) {
        return {
            ...state,
            sleep: failure(state.sleep)
        }
    }
    else if (type === Type.PREVIOUS) {
        return {
            ...state,
            prevSleep: failure(state.prevSleep)
        }
    }
    return state
}

export const changeDialog = (
    state: IReportState = INITIAL_STATE,
    action: IActionType<{ dialog: boolean }>) => {
    const { dialog } = action.payload
    return {
        ...state,
        dialog
    }
}

export const newReportRequest = (
    state: IReportState = INITIAL_STATE,
    action: IActionType<INewReportRequest>) => {
    const {
        depression,
        muscleStrength,
        nocturiaPresence,
        sarcopenia,
        sleepiness,
        startDate,
        endDate,
        comparatorReport
    } = action.payload
    return {
        ...state,
        depression,
        muscleStrength,
        nocturiaPresence,
        sarcopenia,
        sleepiness,
        startDate,
        endDate,
        comparatorReport,
        dialog:false
    }
}
export const createReportRequest = (state: IReportState = INITIAL_STATE) => {
    return { ...state, create: request(state.create) }
}

export const createReportSuccess = (
    state: IReportState = INITIAL_STATE,
    action: IActionType<{ report: Report }>) => {
    const { report } = action.payload

    const listResult = [report].concat(state.list.report)
    const paginator = {
        ...state.list.paginator,
        totalRecords: state.list.paginator.totalRecords + 1
    }

    return {
        ...state,
        create: {
            ...INITIAL_STATE.create,
            report
        },
        list: success({
            ...state.list,
            report: listResult,
            paginator
        }),
    }
}
export const createReportFailure = (state: IReportState = INITIAL_STATE) => {
    return {
        ...state,
        create: failure(state.create)
    }
}
export const changeReportView = (
    state: IReportState = INITIAL_STATE,
    action: IActionType<{ field: string, value: boolean }>) => {
    const { field, value } = action.payload
    return {
        ...state,
        view: {
            ...state.view,
            [field]: value
        }
    }
}

export const resetReportView = (state: IReportState = INITIAL_STATE) => {
    return {
        ...state,
        view: INITIAL_STATE.view
    }
}

export const loadMedicalIndicatorsRequest = (state: IReportState = INITIAL_STATE) => {
    return {
        ...state,
        comorbidities: request(state.comorbidities),
        medicaments: request(state.medicaments)
    }
}

export const comorbiditiesSuccess = (state: IReportState = INITIAL_STATE, action: IActionType<{ data: Comorbidity }>) => {
    const { data } = action.payload
    return {
        ...state,
        comorbidities: success({ ...state.comorbidities, data })
    }
}


export const comorbiditiesFailure = (state: IReportState = INITIAL_STATE) => {
    return {
        ...state,
        comorbidities: failure(state.comorbidities)
    }
}


export const medicamentsSuccess = (state: IReportState = INITIAL_STATE, action: IActionType<{ data: Comorbidity }>) => {
    const { data } = action.payload
    return {
        ...state,
        medicaments: success({ ...state.medicaments, data })
    }
}


export const medicamentsFailure = (state: IReportState = INITIAL_STATE) => {
    return {
        ...state,
        medicaments: failure(state.medicaments)
    }
}
export const loadReportRequest = (state: IReportState = INITIAL_STATE, action: IActionType<{ paginator: IPaginator }>) => {
    const { paginator } = action.payload

    return {
        ...state,
        list: request({ ...state.list, paginator })
    }
}
export const loadReportSuccess = (
    state: IReportState = INITIAL_STATE,
    action: IActionType<{ data: Report[], headers: any }>) => {
    const { data, headers } = action.payload
    const paginator = {
        ...state.list.paginator,
        totalRecords: parseInt(headers['x-total-count'], 10)
    }
    return {
        ...state,
        list: success({ ...state.list, paginator, report: data })
    }
}
export const loadMoreSuccess = (
    state: IReportState = INITIAL_STATE,
    action: IActionType<{ data: Report[], headers: any }>) => {
    const { headers, data } = action.payload
    const paginator = {
        ...state.list.paginator,
        totalRecords: parseInt(headers['x-total-count'], 10)
    }
    const report = state.list.report.concat(data)

    return {
        ...state,
        list: success({
            ...state.list,
            paginator,
            report
        })
    }
}

export const loadReportFailure = (state: IReportState = INITIAL_STATE) => {
    return {
        ...state,
        list: failure(state.list)
    }
}


/* Actions to remove report  */
export const removeReportRequest = (state: IReportState = INITIAL_STATE) => {
    return {
        ...state,
        remove: request({ ...state.list })
    }
}
export const removeReportSuccess = (
    state: IReportState = INITIAL_STATE,
    action: IActionType<{ ids: string[] }>) => {
    const { ids } = action.payload
    const reports = state.list.report

    const updatedReport = reports.filter((report: Report) => !ids?.some((removed: string) => report?.id === removed))

    const paginator = {
        ...state.list.paginator,
        totalRecords: state.list.paginator.totalRecords - 1
    }

    return {
        ...state,
        remove: success({ ...INITIAL_STATE.remove }),
        list: {
            ...state.list,
            report: updatedReport,
            paginator
        }
    }
}
export const removeReportFailure = (state: IReportState = INITIAL_STATE) => {
    return {
        ...state,
        remove: failure(state.list)
    }
}
export const changeDialogRemove = (
    state: IReportState = INITIAL_STATE,
    action: IActionType<{ dialog: boolean, ids: string[] }>) => {
    const { dialog, ids } = action.payload

    return {
        ...state,
        remove: {
            ...state.remove,
            dialog,
            ids
        }
    }
}
const reducer: Reducer<IReportState> = createReducer<IReportState>(INITIAL_STATE, {
    [ReportTypes.RESET]: reset,

    [ReportTypes.CHANGE_PAGINATOR]: changeListPaginator,

    [ReportTypes.LOAD_STEPS_REQUEST]: loadStepsRequest,
    [ReportTypes.LOAD_STEPS_SUCCESS]: loadStepSuccess,
    [ReportTypes.LOAD_STEPS_FAILURE]: loadStepsFailure,

    [ReportTypes.LOAD_ACTIVE_MINUTES_REQUEST]: loadActiveMinutesRequest,
    [ReportTypes.LOAD_ACTIVE_MINUTES_SUCCESS]: loadActiveMinutesSuccess,
    [ReportTypes.LOAD_ACTIVE_MINUTES_FAILURE]: loadActiveMinutesFailure,

    [ReportTypes.LOAD_HEART_RATE_REQUEST]: loadHeartRateRequest,
    [ReportTypes.LOAD_HEART_RATE_SUCCESS]: loadHearRateSuccess,
    [ReportTypes.LOAD_HEART_RATE_FAILURE]: loadHeartRateFailure,

    [ReportTypes.LOAD_SLEEP_REQUEST]: loadSleepRequest,
    [ReportTypes.LOAD_SLEEP_SUCCESS]: loadSleepSuccess,
    [ReportTypes.LOAD_SLEEP_FAILURE]: loadSleepFailure,

    [ReportTypes.CHANGE_DIALOG]: changeDialog,
    [ReportTypes.CHANGE_DIALOG_REMOVE]: changeDialogRemove,

    [ReportTypes.NEW_REPORT_REQUEST]: newReportRequest,
    [ReportTypes.CHANGE_REPORT_VIEW]: changeReportView,
    [ReportTypes.RESET_REPORT_VIEW]: resetReportView,

    [ReportTypes.LOAD_MEDICAL_INDICATORS_REQUEST]: loadMedicalIndicatorsRequest,
    [ReportTypes.LOAD_COMORBIDITY_SUCCESS]: comorbiditiesSuccess,
    [ReportTypes.LOAD_COMORBIDITY_FAILURE]: comorbiditiesFailure,
    [ReportTypes.LOAD_MEDICAMENT_SUCCESS]: medicamentsSuccess,
    [ReportTypes.LOAD_MEDICAMENT_FAILURE]: medicamentsFailure,
    [ReportTypes.RESET_REPORT_VIEW]: resetReportView,

    [ReportTypes.LOAD_REPORT_REQUEST]: loadReportRequest,
    [ReportTypes.LOAD_REPORT_SUCCESS]: loadReportSuccess,
    [ReportTypes.LOAD_REPORT_FAILURE]: loadReportFailure,

    [ReportTypes.LOAD_MORE_REQUEST]: loadReportRequest,
    [ReportTypes.LOAD_MORE_SUCCESS]: loadMoreSuccess,
    [ReportTypes.LOAD_MORE_FAILURE]: loadReportFailure,

    [ReportTypes.REMOVE_REPORT_SUCCESS]: removeReportSuccess,
    [ReportTypes.REMOVE_REPORT_REQUEST]: removeReportRequest,
    [ReportTypes.REMOVE_REPORT_FAILURE]: removeReportFailure,

    [ReportTypes.CREATE_REPORT_SUCCESS]: createReportSuccess,
    [ReportTypes.CREATE_REPORT_REQUEST]: createReportRequest,
    [ReportTypes.CREATE_REPORT_FAILURE]: createReportFailure
})

export default reducer
